/**
 * extractUpdateOutline — token-aware UPDATE statement walker. Returns the
 * primary target text plus byte spans for the optional multi-table tail
 * (MySQL `UPDATE t1 [, t2 | JOIN t2 ON …] SET …`), the SET assignment list,
 * the optional FROM clause (PG / MSSQL `UPDATE … SET … FROM …`), the WHERE
 * predicate, and the optional RETURNING list (PG / Oracle / SQLite ≥ 3.35).
 *
 * Pure / structural — uses `tokenSkip` so dialect-specific quoting (backticks,
 * brackets, dollar-tags, hash comments) is invisible to brace counting
 * (VQE-RO9 — no regex parsing).
 *
 * Reuses the shared low-level token primitives in `../insert/insertParseHelpers`.
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { skipContextFor } from '../../../../sql-resolver/classify/tokenSkip';
import {
  readWord,
  skipWsAndComments,
} from '../insert/insertParseHelpers';
import { findTopLevelKeyword, readQualifiedTarget, spanHasJoinOrComma, trimSpan, type KeywordHit } from '../common/statementScan';

export interface UpdateOutline {
  /** True only when the leading word in the statement range is `UPDATE`. */
  readonly hasUpdate: boolean;
  /** Primary target table text exactly as written. */
  readonly targetText: string | null;
  readonly targetStart: number | null;
  readonly targetEnd: number | null;
  /**
   * Span between the target's end and the `SET` keyword's start, whitespace-
   * trimmed. Non-empty only when the UPDATE has a multi-table tail (MySQL
   * `UPDATE t1 JOIN t2 ON … SET …` or `UPDATE t1, t2 SET …`). Null when the
   * target is immediately followed by SET.
   */
  readonly multiTableStart: number | null;
  readonly multiTableEnd: number | null;
  /** SET assignment list span (excluding the keyword itself). */
  readonly setStart: number | null;
  readonly setEnd: number | null;
  /** PG / MSSQL `FROM …` clause body span (excluding the keyword). */
  readonly fromStart: number | null;
  readonly fromEnd: number | null;
  /** WHERE predicate span (excluding the keyword). Ends BEFORE a trailing
   *  MySQL single-table `ORDER BY` / `LIMIT` (I7). */
  readonly whereStart: number | null;
  readonly whereEnd: number | null;
  /** Trailing `ORDER BY …` span (MySQL single-table UPDATE). Non-null = present. */
  readonly orderByStart: number | null;
  readonly orderByEnd: number | null;
  /** Trailing `LIMIT …` span (MySQL single-table UPDATE). Non-null = present. */
  readonly limitStart: number | null;
  readonly limitEnd: number | null;
  /** RETURNING expression list span (excluding the keyword). */
  readonly returningStart: number | null;
  readonly returningEnd: number | null;
  /**
   * True only when the statement joins a relation OTHER than the target — a
   * MySQL `UPDATE … JOIN …` / comma multi-table tail, or a PG/MSSQL
   * `UPDATE … FROM …` whose FROM has a JOIN or a second relation. A bare
   * trailing alias (`UPDATE film f`) or an MSSQL self-FROM (`FROM the-target`)
   * is NOT a join tail. Drives the empty-WHERE "every row" scope warning: with
   * a join tail only join-matched rows are touched, so that overclaim is
   * suppressed (I8).
   */
  readonly hasJoinTail: boolean;
}

const EMPTY_OUTLINE: UpdateOutline = {
  hasUpdate: false,
  targetText: null, targetStart: null, targetEnd: null,
  multiTableStart: null, multiTableEnd: null,
  setStart: null, setEnd: null,
  fromStart: null, fromEnd: null,
  whereStart: null, whereEnd: null,
  orderByStart: null, orderByEnd: null,
  limitStart: null, limitEnd: null,
  returningStart: null, returningEnd: null,
  hasJoinTail: false,
};

export function extractUpdateOutline(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): UpdateOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const ctx = skipContextFor(options);

  let i = skipWsAndComments(source, start, end);
  const updateWord = readWord(source, i, end);
  if (!updateWord || updateWord.upper !== 'UPDATE') return EMPTY_OUTLINE;
  i = skipWsAndComments(source, updateWord.end, end);

  const target = readQualifiedTarget(source, i, end);
  if (!target) return { ...EMPTY_OUTLINE, hasUpdate: true };

  const setMatch = findTopLevelKeyword(source, target.end, end, ctx, ['SET']);
  if (!setMatch) {
    return {
      ...EMPTY_OUTLINE,
      hasUpdate: true,
      targetText: target.text,
      targetStart: target.start,
      targetEnd: target.end,
    };
  }

  const multiTable = trimSpan(source, target.end, setMatch.start);

  const setBodyStart = setMatch.end;
  // ORDER / LIMIT are trailing clauses in MySQL single-table UPDATE ONLY
  // (I7). `LIMIT` is NOT a reserved word in MSSQL/Oracle/PG/SQLite — it's a
  // legal unquoted column name there (`SET limit = 5`) — so recognizing it as
  // a tail keyword on those engines would truncate the SET/WHERE spans and
  // silently misclassify the statement's scope. Gate to MySQL so every other
  // engine parses byte-identically to before I7.
  const isMysql = options.engine === 'mysql';
  const tailKeywords = isMysql ? MYSQL_TAIL_KEYWORDS : BASE_TAIL_KEYWORDS;
  const fromMatch = findTopLevelKeyword(source, setBodyStart, end, ctx, tailKeywords);
  const setEnd = fromMatch ? fromMatch.start : end;

  let fromStart: number | null = null;
  let fromEnd: number | null = null;
  let whereStart: number | null = null;
  let whereEnd: number | null = null;
  let orderByStart: number | null = null;
  let orderByEnd: number | null = null;
  let limitStart: number | null = null;
  let limitEnd: number | null = null;
  let returningStart: number | null = null;
  let returningEnd: number | null = null;

  let cursor = fromMatch ? fromMatch.end : setEnd;
  let nextKw: KeywordHit | null = fromMatch;
  while (nextKw) {
    if (nextKw.upper === 'FROM') {
      const next = findTopLevelKeyword(
        source, cursor, end, ctx,
        isMysql ? ['WHERE', 'ORDER', 'LIMIT', 'RETURNING'] : ['WHERE', 'RETURNING'],
      );
      fromStart = cursor;
      fromEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      nextKw = next;
      continue;
    }
    if (nextKw.upper === 'WHERE') {
      const next = findTopLevelKeyword(
        source, cursor, end, ctx,
        isMysql ? ['ORDER', 'LIMIT', 'RETURNING'] : ['RETURNING'],
      );
      whereStart = cursor;
      whereEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      nextKw = next;
      continue;
    }
    if (nextKw.upper === 'ORDER') {
      const next = findTopLevelKeyword(source, cursor, end, ctx, ['LIMIT', 'RETURNING']);
      orderByStart = cursor;
      orderByEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      nextKw = next;
      continue;
    }
    if (nextKw.upper === 'LIMIT') {
      const next = findTopLevelKeyword(source, cursor, end, ctx, ['RETURNING']);
      limitStart = cursor;
      limitEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      nextKw = next;
      continue;
    }
    if (nextKw.upper === 'RETURNING') {
      returningStart = cursor;
      returningEnd = end;
      break;
    }
    break;
  }

  const hasJoinTail =
    (fromStart !== null && fromEnd !== null && spanHasJoinOrComma(source, fromStart, fromEnd, ctx)) ||
    (multiTable !== null && spanHasJoinOrComma(source, multiTable.start, multiTable.end, ctx));

  return {
    hasUpdate: true,
    targetText: target.text,
    targetStart: target.start,
    targetEnd: target.end,
    multiTableStart: multiTable ? multiTable.start : null,
    multiTableEnd: multiTable ? multiTable.end : null,
    setStart: setBodyStart,
    setEnd,
    fromStart,
    fromEnd,
    whereStart,
    whereEnd,
    orderByStart,
    orderByEnd,
    limitStart,
    limitEnd,
    returningStart,
    returningEnd,
    hasJoinTail,
  };
}

/**
 * Clause keywords that terminate the SET body (in statement order).
 * `ORDER` / `LIMIT` are only real trailing-clause keywords on MySQL single-
 * table UPDATE (I7) — on MSSQL/Oracle/PG/SQLite `LIMIT` is a legal unquoted
 * column/identifier, so those engines must use the base set to stay
 * byte-identical to the pre-I7 parse.
 */
const BASE_TAIL_KEYWORDS: readonly string[] = ['FROM', 'WHERE', 'RETURNING'];
const MYSQL_TAIL_KEYWORDS: readonly string[] = ['FROM', 'WHERE', 'ORDER', 'LIMIT', 'RETURNING'];

